// import JSZip from 'jszip';

// zip.js est chargé en global (script assets/js/zip.js), pas en import ES module.
// En mode module, `zip` nu provoque ReferenceError : toujours passer par window.zip.

import { sequence } from './promises';
import { dataURItoBlob } from './image';
import config from '../config';

const GENERIC_FILE_EXCLUDES = [/__MACOSX/];
export const MEDIAS_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'gif', 'mp4', 'mp3', 'opus', 'aac', 'm4a', 'mov'];

function getZipApi() {
    const zipApi = typeof window !== 'undefined' ? window.zip : null;
    if(!zipApi) {
        throw new ReferenceError('zip is not defined');
    }
    return zipApi;
}

/** Charge zip.js une fois (même script que l'import conversation). */
export function ensureZipLoaded() {
    if(typeof window !== 'undefined' && window.zip) {
        return Promise.resolve(window.zip);
    }

    return new Promise((resolve, reject) => {
        if(typeof document === 'undefined') {
            reject(new ReferenceError('zip is not defined'));
            return;
        }

        const existing = document.querySelector('script[data-monlivresms-zipjs]');
        if(existing) {
            const onLoad = () => {
                existing.removeEventListener('load', onLoad);
                existing.removeEventListener('error', onError);
                if(window.zip) {
                    resolve(window.zip);
                } else {
                    reject(new ReferenceError('zip is not defined'));
                }
            };
            const onError = () => {
                existing.removeEventListener('load', onLoad);
                existing.removeEventListener('error', onError);
                reject(new Error('zip.js load failed'));
            };
            existing.addEventListener('load', onLoad);
            existing.addEventListener('error', onError);
            return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = `${config.staticUrl}assets/js/zip.js`;
        script.dataset.monlivresmsZipjs = '1';
        script.onload = () => {
            if(window.zip) {
                resolve(window.zip);
            } else {
                reject(new ReferenceError('zip is not defined'));
            }
        };
        script.onerror = () => reject(new Error('zip.js load failed'));
        document.head.appendChild(script);
    });
}

export function readArchive(file, options = { includeRegexFiles: [], excludeRegexFiles: [] }, onProgress = percentile => {}) {
    console.log('readArchive launched...', options);

    return ensureZipLoaded().then(zipApi => {
        zipApi.workerScriptsPath = `${config.staticUrl}/assets/js/`;
        onProgress(1);
        return new Promise((resolve, reject) => {
            const reader = new zipApi.ZipReader(new zipApi.BlobReader(file));
            reader.getEntries().then(entries => {
                entries = entries
                    .filter(entry => (options.includeRegexFiles || []).some(includeRegex => includeRegex.test(entry.filename)))
                    .filter(entry => (options.excludeRegexFiles || []).concat(GENERIC_FILE_EXCLUDES).every(excludeRegex => !excludeRegex.test(entry.filename)));

                const percentile = 100 / entries.length;

                getFileContentRecursive(entries, results => {
                    resolve(results.map(entry => {
                        return {
                            ...entry,
                            filename: entry.path.split('/').pop(),
                        };
                    }));
                }, onProgress, reject);
            }).catch(reject);
        });
    });
}

function getFileContentRecursive(entries, onSuccess = () => {}, onProgress = percentile => {}, onError = err => {}, index = 0, results = []) {
    const entry = entries[index];
    if(entry) {
        getFileContent(entry).then(data => {
            onProgress(100 / entries.length);
            getFileContentRecursive(entries, onSuccess, onProgress, onError, index + 1, [...results, data]);
        }).catch(err => {
            onError(err);
        });
    } else {
        onSuccess(results);
    }
}

/**
 * Extrait d'un zip uniquement les médias dont le basename matche missingFilenames
 * (insensible à la casse). Remonte les fichiers avec le nom attendu (casing du rapport)
 * pour coller aux références CSV / S3 sans reparser la conversation.
 */
export function extractMissingMediasFromArchive(file, missingFilenames = [], onProgress = () => {}) {
    const wantedByLower = new Map();
    (missingFilenames || []).forEach(name => {
        if(name) {
            wantedByLower.set(String(name).toLowerCase(), name);
        }
    });
    if(!wantedByLower.size) {
        return Promise.resolve([]);
    }

    return ensureZipLoaded().then(zipApi => {
        zipApi.workerScriptsPath = `${config.staticUrl}/assets/js/`;
        onProgress(1);

        return new Promise((resolve, reject) => {
            const reader = new zipApi.ZipReader(new zipApi.BlobReader(file));
            reader.getEntries().then(entries => {
                const matched = [];
                const seen = new Set();
                (entries || []).forEach(entry => {
                    if(!entry || entry.directory) {
                        return;
                    }
                    const basename = String(entry.filename || '').split('/').pop();
                    const expectedName = wantedByLower.get(basename.toLowerCase());
                    if(!expectedName || seen.has(expectedName.toLowerCase())) {
                        return;
                    }
                    if(GENERIC_FILE_EXCLUDES.some(regex => regex.test(entry.filename))) {
                        return;
                    }
                    seen.add(expectedName.toLowerCase());
                    matched.push({ entry, expectedName });
                });

                if(!matched.length) {
                    resolve([]);
                    return;
                }

                const percentile = 100 / matched.length;
                sequence(matched, ({ entry, expectedName }) => {
                    return getFileContent(entry).then(data => {
                        onProgress(percentile);
                        return {
                            ...data,
                            filename: expectedName,
                            path: entry.filename,
                        };
                    });
                }).then(resolve).catch(reject);
            }).catch(reject);
        });
    });
}

function getFileContent(entry) {
    return new Promise((resolve, reject) => {
        try {
            const zipApi = getZipApi();
            const ext = ((entry.filename || '').split('.').pop() || '').toLowerCase();
            if(MEDIAS_EXTENSIONS.includes(ext)) {
                let type = '';
                switch(ext) {
                    case 'mp4':
                    case 'webm':
                    case 'mov':
                        type = 'video';
                        break;

                    case 'mp3':
                    case 'opus':
                    case 'aac':
                    case 'm4a':
                    case 'ogg':
                        type = 'audio';
                        break;

                    case 'jpg':
                    case 'jpeg':
                    case 'png':
                    case 'gif':
                    case 'webp':
                    default:
                        type = 'image';
                }
                entry.getData(new zipApi.Data64URIWriter(`${type}/${ext.replace('jpeg', 'jpg')}`)).then(data => {
                    resolve({ path: entry.filename, content: dataURItoBlob(data), type });
                }).catch(reject);
            } else {
                console.log(`Missing content type for ${ext} -> Read as text`, entry);
                entry.getData(new zipApi.TextWriter(null)).then(text => {
                    resolve({ path: entry.filename, content: text, type: 'text' });
                }).catch(reject);
            }
        } catch (err) {
            reject(err);
        }
    });
}
